/**
 * Type Definitions for Matters Service
 *
 * This module defines all TypeScript interfaces and types used by the
 * matters service, including API requests/responses and domain models.
 */

import { z } from 'zod';
import { MatterTypeSlug } from '@/lib/services/matter-types/types';
import type { PmsProvider } from '@/lib/services/integrations/pms';
import type { OutlookSendIdentity } from '@/lib/services/communications/types';

// ============================================================================
// Enums and Constants
// ============================================================================

// ============================================================================
// Role Types (matching backend MatterRoleDto)
// ============================================================================

/**
 * User summary within a role assignment
 * Matches backend UserSummaryDto
 */
export interface RoleUserSummary {
  id: number;
  /**
   * Nullable (LF-6015). `roles.service.ts` builds this with
   * `plainToInstance(UserSummaryDto, role.lawFirmUser)` — a straight projection
   * of the entity's nullable name columns, with no status filter — and
   * `getAssignableFirmUsers` deliberately keeps `invited` users in the
   * assignment picker, so a nameless user can genuinely hold a matter role.
   * The sibling `MatterRole` / `ActingSolicitor` in `lib/services/tasks/types.ts`
   * already type the same columns this way.
   */
  firstName: string | null;
  lastName: string | null;
  email: string;
}

/**
 * Role assignment on a matter
 * Matches backend MatterRoleDto from DetailedMatterResponseDto
 */
export interface MatterRoleAssignment {
  user: RoleUserSummary;
  role: 'acting_solicitor' | 'assisting_member';
  assignedAt: string;
}

// ============================================================================
// Stage Types (matches backend matter-lifecycle types)
// ============================================================================

export type StageType =
  | 'initial_setup'
  | 'discovery'
  | 'representation'
  | 'collection'
  | 'distribution';

export type StageCompletionState =
  | 'complete'
  | 'in_progress'
  | 'not_started'
  | 'not_applicable';

export interface StageProgress {
  completed: number;
  total: number;
  percent: number;
}

export interface MatterStage {
  stage: StageType;
  completionState: StageCompletionState;
  statusDetail: string | null;
  // Optional for backwards compatibility with matters whose persisted stages
  // predate the progress/attention contract. Use `getStageIndicators(stage)`
  // (lib/services/matters/stage-indicators.ts) to read with safe defaults.
  progress?: StageProgress;
  requiresAttention?: boolean;
}

// ============================================================================
// Checklist Types (for non-estate matters)
// ============================================================================

export interface ChecklistItem {
  id: string; // UUID
  text: string; // Item description
  isComplete: boolean;
}

// ============================================================================
// Core Matter Types
// ============================================================================

export interface PostalAddress {
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export interface Matter {
  id: number;
  type?: string; // Matter type slug
  typeLabel?: string; // Matter type display name
  lawFirmInternalReference?: string;
  notes?: string;
  isArchived: boolean;
  decedentTitle?: string;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  decedentDateOfBirth?: string;
  decedentDateOfDeath?: string;
  willDate?: string | null;
  deathCertificateRegistrationNumber?: string | null;
  decedentAddress?: PostalAddress;
  actingSolicitorId?: number;
  actingSolicitor?: {
    id: number;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  isGrantRequired?: boolean;
  hasWill?: boolean;
  hasDeathCertificate?: boolean;
  deathCertificateIsCertifiedCopy?: boolean;
  legalPersonalRepresentatives?: Array<{
    id: number;
    firstName: string;
    middleName?: string;
    lastName: string;
    isLegalPersonalRepresentative?: boolean;
    legalPersonalRepresentativeRole?: string | null;
  }>;
  advertisedAt?: string | null;
  isGrantLodged?: boolean;
  grantLodgedDate?: string;
  lawFirm?: {
    id: number;
    name: string;
  };
  /**
   * The office running this matter, or `null` for firm-wide (VALE-549).
   *
   * `null` is a permanently valid state rather than a gap — every matter that
   * predates its firm's offices carries it, and nothing derives one from the
   * acting solicitor. It decides which of the firm's letterheads the matter's
   * correspondence prints on.
   */
  officeId?: number | null;
  /**
   * The assigned office's name, resolved server-side off the matter's own
   * office row — so an office that has since been **closed** still displays
   * its name here even though {@link Matter.offices} no longer offers it.
   *
   * Detail read only; absent on list rows.
   */
  officeName?: string | null;
  /**
   * The firm's **active** offices, in the firm's own order — the options the
   * Office picker renders. Empty means the firm has no offices, and the Office
   * row is omitted entirely rather than rendering an empty picker.
   *
   * Detail read only; absent on list rows.
   */
  offices?: Array<{ id: number; name: string }>;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  metadata?: {
    clientNames?: string[];
    description?: string;
    pms?: {
      /**
       * The PMS this matter is synced with, set once a sync or import completes.
       *
       * Bound to {@link PmsProvider} rather than restating a union: the hand-typed list here omitted
       * `actionstep` — which the backend has written at runtime since [ASTEP-16] — so every
       * ActionStep-synced matter was mistyped, and the document-import gate could not name the
       * provider without a type error (LF-6344).
       */
      source?: PmsProvider;
      requiresReview?: boolean;
      reviewedAt?: string;
      reviewedBy?: number;
      transferredFromArchivedMatterId?: number;
      matterTypeId?: string;
      matterTypeLabel?: string;
      stateOfMatter?: string;
      representativeOption?: string; // Client role for non-estate-administration matters
    };
  };

  // Stage progress + attention indicators (drives sidebar progress bars + dots)
  stages?: MatterStage[];

  // Related data
  contactsCount?: number;
  inventoryCount?: number;
  tasksCount?: number;
  filesCount?: number;

  // Current user's task for this matter (from backend)
  currentUserTask?: {
    id: number;
    scheduledDate: string;
    position: number;
    description?: string | null;
  };

  roles?: MatterRoleAssignment[];

  estateBankAccount?: {
    accountName: string;
    bsb: string;
    accountNumber: string;
  };
  useTrustAccountForCollection?: boolean;
  tags?: string[];
  checklist?: ChecklistItem[]; // For non-estate matters

  // Grant of Representation fields (QLD/VIC specific)
  grantOfRepresentationCode?: string; // Access code for QLD, Unique identifier code for VIC
  grantOfRepresentationReference?: string; // Application number for VIC only

  // PMS sync status
  pmsSynced?: boolean; // Whether matter has been synced to PMS (Smokeball, etc.)
  pmsProviderName?: PmsProvider | 'leap' | 'clio'; // PMS provider for this matter's law firm

  /**
   * Whether this matter can send email at all, and from whose mailbox — resolved
   * by the backend using the same decision the send path makes.
   *
   * `null` for every firm not on Outlook transport, which is almost all of them.
   * When `blocked` is true, every send control on this matter must be disabled
   * with an explanation: the send would be refused with a 409.
   *
   * **Never re-derive this by comparing email addresses.** The backend matches
   * the acting solicitor's mailbox by user id; an address comparison diverges on
   * aliases, and in the dangerous direction — blocking a send that would have
   * worked. Read it via `useOutlookMatterSendGate`.
   */
  outlookSendIdentity?: OutlookSendIdentity | null;

  // Clients (for non-estate-administration matters)
  clients?: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    organisationName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  }>;

  // Demo matter flag — when true, every outbound external email is redirected
  // server-side back to the authenticated user. UI surfaces a visual marker
  // and a redirect disclaimer on every send.
  isDemo?: boolean;
  // Backend currently emits this but no UI sets it. The frontend ignores it
  // and always shows the current user's email in the disclaimer.
  demoRecipientEmail?: string | null;
}

export interface GenericContact {
  id: number;
  firstName: string;
  lastName: string;
}

export interface MatterSummary {
  id: number;
  type?: string; // Slug: "estate-administration", etc.
  typeLabel?: string; // Display: "Estate Administration"
  lawFirmInternalReference?: string;
  beneficiaries?: GenericContact[];
  decedentName: string;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  legalPersonalRepresentativeName?: string;
  legalPersonalRepresentative?: string[]; // New backend field - array of LPR names
  hasWill?: boolean;
  dateOfDeath?: string;
  createdAt: string;
  lastUpdated: string;
  assignedTo?: string[];
  tasksOverdue?: number;
  progress?: number;
  isArchived?: boolean;
  legalPersonalRepresentatives?: Array<{
    id: number;
    firstName: string;
    middleName?: string;
    lastName: string;
    isLegalPersonalRepresentative?: boolean;
    legalPersonalRepresentativeRole?: string | null;
  }>;
  stages?: MatterStage[];
  actingSolicitor?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
  } | null;
  roles?: Array<{
    id: number;
    firstName: string | null;
    lastName: string | null;
    role: string;
  }>;
  tags?: string[];
  notes?: string;
  metadata?: {
    clientNames?: string[];
    description?: string;
  };
  // Clients (for non-estate-administration matters)
  clients?: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    organisationName?: string | null;
  }>;

  isDemo?: boolean;
  demoRecipientEmail?: string | null;

  /**
   * Whether this matter can send email at all, and from whose mailbox — the
   * same field `Matter` carries, now served by the list endpoint too so a
   * lawyer scanning their matters can see which ones cannot send (VALE-347).
   *
   * `null` for every firm not on Outlook transport. Render its
   * `blockedMessage` verbatim; never re-derive the block from an email address,
   * nor the sentence from `blockedReason`.
   */
  outlookSendIdentity?: OutlookSendIdentity | null;

  /**
   * Whether this matter is synced to the firm's CURRENT PMS, and which
   * provider — the same fields `Matter` carries, now served by the list
   * endpoint too so a lawyer scanning their matters can see which are in the
   * PMS without opening each one (VALE-475).
   *
   * `pmsProviderName` is absent for every firm with no enabled PMS. Gate any
   * mark on `pmsSynced` alone — it is already scoped to the firm's *current*
   * integration (see `checkMatterPmsStatus` backend-side), so a matter
   * stranded on a since-retired provider reads `false` here, never `true`.
   */
  pmsSynced?: boolean;
  pmsProviderName?: PmsProvider | 'leap' | 'clio';
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface CreateMatterRequest {
  type?: MatterTypeSlug;
  lawFirmInternalReference?: string;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  decedentDateOfBirth?: string;
  decedentDateOfDeath?: string;
  decedentAddress?: PostalAddress;
  notes?: string;
  roles?: Array<{
    userId: number;
    role: 'acting_solicitor' | 'assisting_member';
  }>;
  metadata?: {
    clientNames?: string[];
    description?: string;
  };
}

export interface UpdateMatterRequest {
  lawFirmInternalReference?: string;
  decedentTitle?: string;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  /** Send `YYYY-MM-DD` to set, or `null` to clear. `''` is rejected by the API. */
  decedentDateOfBirth?: string | null;
  /** Send `YYYY-MM-DD` to set, or `null` to clear. `''` is rejected by the API. */
  decedentDateOfDeath?: string | null;
  /** Send `YYYY-MM-DD` to set, or `null` to clear. `''` is rejected by the API. */
  willDate?: string | null;
  /** Send a string to set, or `null` to clear an erroneously-populated value. */
  deathCertificateRegistrationNumber?: string | null;
  decedentAddress?: PostalAddress;
  /**
   * The office running this matter — an id from {@link Matter.offices}, or
   * `null` to clear it back to firm-wide (VALE-549). Validated server-side
   * against the acting firm; a closed office already on the matter is still
   * accepted, so editing any other field on it is never refused.
   */
  officeId?: number | null;
  notes?: string;
  isArchived?: boolean;
  isGrantRequired?: boolean;
  isWillRequired?: boolean;
  advertisedAt?: string | null;
  isAdvertismentRequired?: boolean;
  advertisedForDistributionAt?: string | null;
  isAdvertismentForDistributionRequired?: boolean;
  isGrantLodged?: boolean;
  clientIds?: number[]; // Contact IDs to assign as clients (non-estate-administration matters only)
  estateBankAccount?: {
    accountName: string;
    bsb: string;
    accountNumber: string;
  };
  useTrustAccountForCollection?: boolean;
  roles?: Array<{
    userId: number;
    role: 'acting_solicitor' | 'assisting_member';
  }>;
  metadata?: {
    clientNames?: string[];
    description?: string;
    pms?: {
      matterTypeId?: string;
      stateOfMatter?: string;
      representativeOption?: string; // Client role for non-estate-administration matters
    };
  };
  checklist?: ChecklistItem[]; // Full array replacement

  // Grant of Representation fields (QLD/VIC specific)
  grantOfRepresentationCode?: string; // Access code for QLD, Unique identifier code for VIC
  grantOfRepresentationReference?: string; // Application number for VIC only
}

export interface MatterFilters {
  /** @deprecated Use `q` for backend fuzzy search instead */
  search?: string;
  /** Backend fuzzy search query - searches matter reference, deceased name, and LPR names */
  q?: string;
  isArchived?: boolean;
  includeArchived?: 'only' | 'true' | 'false'; // New: 'only' for archived view, 'true' to include all
  assignedToMe?: boolean;
  hasTask?: boolean;
  page?: number;
  limit?: number;
  /** Matter type slugs for filtering (e.g., ['estate-administration', 'estate-planning']) */
  types?: string[];
  /** Team member IDs for filtering */
  teamMemberIds?: number[];
  /** Sort field - matches backend: updatedAt, createdAt, lawFirmInternalReference */
  sortBy?: 'updatedAt' | 'createdAt' | 'lawFirmInternalReference';
  /** Sort direction - matches backend: ASC, DESC */
  sortOrder?: 'ASC' | 'DESC';
  /** Matter tags for filtering (comma-separated when sent to backend) */
  tags?: string[];
  /** When true, only return demo matters (DEMO-001 etc.). */
  isDemo?: boolean;
}

export interface MatterListResponse {
  matters: MatterSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// ============================================================================
// Validation Schemas
// ============================================================================

export const PostalAddressSchema = z.object({
  streetAddress: z.string().optional(),
  suburb: z.string().optional(),
  state: z.string().optional(),
  postcode: z.string().optional(),
  country: z.string().optional()
});

export const CreateMatterRequestSchema = z.object({
  type: z.nativeEnum(MatterTypeSlug).optional(),
  lawFirmInternalReference: z.string().optional(),
  decedentFirstName: z.string().min(1, 'First name is required').optional(),
  decedentMiddleName: z.string().optional(),
  decedentLastName: z.string().min(1, 'Last name is required').optional(),
  decedentDateOfBirth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date of birth must be YYYY-MM-DD')
    .optional(),
  decedentDateOfDeath: z.string().optional(),
  decedentAddress: PostalAddressSchema.optional(),
  notes: z.string().optional(),
  roles: z
    .array(
      z.object({
        userId: z.number(),
        role: z.enum(['acting_solicitor', 'assisting_member'])
      })
    )
    .optional()
});

export const UpdateMatterRequestSchema = z.object({
  lawFirmInternalReference: z.string().optional(),
  decedentTitle: z.string().optional(),
  decedentFirstName: z.string().optional(),
  decedentMiddleName: z.string().optional(),
  decedentLastName: z.string().optional(),
  /*
    Nullable, because clearing a recorded date has to be expressible (VALE-90).
    The backend has always accepted `null` here — `UpdateMatterDto` guards both
    columns with `@Matches(/^\d{4}-\d{2}-\d{2}$/)` alongside `@IsOptional()`,
    and `@IsOptional()` skips its validators for `null` / `undefined` only. An
    empty string is a *value*, so it reaches `@Matches` and 400s; `null` passes
    and nulls the column. Until this said so, no surface could clear a date at
    all: the omission was in the client schema, not in the API.
  */
  decedentDateOfBirth: z.string().nullable().optional(),
  decedentDateOfDeath: z.string().nullable().optional(),
  willDate: z.string().nullable().optional(),
  deathCertificateRegistrationNumber: z
    .string()
    .max(64, 'Must be 64 characters or fewer')
    .nullable()
    .optional(),
  decedentAddress: PostalAddressSchema.optional(),
  /*
    Nullable for the same reason the dates above are: clearing a matter's office
    is a real edit meaning *firm-wide*, and the backend's `@IsOptional()` skips
    its `@IsInt()` for `null` (VALE-549). A key missing from this schema is
    stripped from the request without erroring, so the field would appear to
    save and change nothing.
  */
  officeId: z.number().int().positive().nullable().optional(),
  notes: z.string().optional(),
  archived: z.boolean().optional(), // Deprecated
  isArchived: z.boolean().optional(), // Backend expects this field name
  isGrantRequired: z.boolean().optional(),
  isWillRequired: z.boolean().optional(),
  advertisedAt: z.string().nullable().optional(),
  isAdvertismentRequired: z.boolean().optional(),
  advertisedForDistributionAt: z.string().nullable().optional(),
  isAdvertismentForDistributionRequired: z.boolean().optional(),
  isGrantLodged: z.boolean().optional(),
  scheduledDate: z.string().optional(),
  estateBankAccount: z
    .object({
      bsb: z
        .string()
        .length(6, 'Must be 6 numbers')
        .regex(/^\d{6}$/, 'Must be numbers only'),
      accountNumber: z
        .string()
        .min(6, 'Must be between 6 and 10 numbers')
        .max(10, 'Must be between 6 and 10 numbers')
        .regex(/^\d{6,10}$/, 'Must be numbers only'),
      accountName: z.string().min(4, 'Must be at least 4 characters')
    })
    .optional(),
  useTrustAccountForCollection: z.boolean().optional(),
  metadata: z
    .object({
      clientNames: z.array(z.string()).optional(),
      description: z.string().optional(),
      pms: z
        .object({
          matterTypeId: z.string().optional(),
          stateOfMatter: z.string().optional(),
          representativeOption: z.string().optional()
        })
        .optional()
    })
    .optional(),
  clientIds: z.array(z.number()).optional(),
  roles: z
    .array(
      z.object({
        userId: z.number(),
        role: z.enum(['acting_solicitor', 'assisting_member'])
      })
    )
    .optional(),
  checklist: z
    .array(
      z.object({
        id: z.string(),
        text: z.string(),
        isComplete: z.boolean()
      })
    )
    .optional(),
  grantOfRepresentationCode: z.string().optional(),
  grantOfRepresentationReference: z.string().optional()
});

// ============================================================================
// Action Result Types
// ============================================================================

export interface MatterActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  validationErrors?: Record<string, string[]>;
}
